import { useCallback, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  selectSequenceVariables,
  selectSelectedElement,
  selectSelectedElementType,
  updateSelectedInteraction,
  updateSelectedInteractionProperty,
  updateSelectedTakeSequence,
} from "../../app/slices/TrainingSlice";
import {
  closeQuizFeedback,
  openQuizFeedback,
  selectIsQuizFeedbackOpen,
  selectQuizFeedbackParentQuizId,
  setFocusedSequenceNodeId,
} from "../../app/slices/GlobalSlice";
import { supportsQuizFeedback } from "../../models/QuizFeedbackHelper";
import { selectAllowWrongAnswersWhitelisted } from "../../app/slices/UserSlice";
import Interaction, {
  InteractionType,
} from "../../models/interactions/Interaction";
import { InteractionProperty } from "../../models/InteractionProperty";
import EditorColumn, {
  EditorColumnFieldGroup,
  EditorColumnHeader,
  EditorColumnSection,
} from "../editor_columns/EditorColumn";
import InteractionPropertyView from "./InteractionPropertyView";
import InteractionWarningsView from "../validation/InteractionWarningsView";
import PiInputfield from "../../components/PiInputField";
import { useTranslation } from "react-i18next";
import {
  selectErrorsForInteraction,
  selectWarningsForInteraction,
} from "../../app/slices/ValidationSlice";
import InteractionErrorsView from "../validation/error_log/InteractionErrorsView";
import PiButton from "../../components/PiButton";
import GTranslateOutlinedIcon from "@mui/icons-material/GTranslateOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import TouchAppOutlinedIcon from "@mui/icons-material/TouchAppOutlined";
import useInteractionsTranslation from "./InteractionsTranslation.hook";
import { CircularProgress } from "@mui/material";
import { Bounce, toast } from "react-toastify";
import { RootState } from "../../app/Store";
import SequenceOutliner from "../sequence/SequenceOutliner";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import SequenceVariablesManager from "../sequence/variables/SequenceVariablesManager";
import CodeIcon from "@mui/icons-material/Code";
import {
  isBuiltInVariable,
} from "../../models/interactions/BuiltInSequenceVariables";
import VariableItem from "../sequence/variables/VariableItem";
import { ISequence } from "../../models/interactions/Sequence";
import SequenceCanvasActionsPanel from "./SequenceCanvasActionsPanel";
import { getInteractionFromFactory } from "../interaction_factory/InteractionFactory";
export default function InteractionPropertiesColumn() {
  const interaction = useSelector(selectSelectedElement) as
    | Interaction
    | undefined;

  if (!interaction) return null;

  return <InteractionPropertiesColumnContent interaction={interaction} />;
}

function InteractionPropertiesColumnContent({
  interaction,
}: {
  interaction: Interaction;
}) {
  const sequenceVariables = useSelector(selectSequenceVariables);
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const selectedElementType = useSelector(selectSelectedElementType);

  const { translateAll } = useInteractionsTranslation();
  const warnings = useSelector((state: RootState) =>
    selectWarningsForInteraction(state, interaction.id)
  );

  const errors = useSelector((state: RootState) =>
    selectErrorsForInteraction(state, interaction.id)
  );

  const onChange = useCallback(
    (interactionProperty: InteractionProperty) => {
      dispatch(updateSelectedInteractionProperty(interactionProperty));
    },
    [dispatch]
  );

  const onUpdateInteraction = useCallback(
    (updatedInteraction: Interaction) => {
      dispatch(updateSelectedInteraction(updatedInteraction));
    },
    [dispatch]
  );

  const isAllowWrongAnswersWhitelisted = useSelector(
    selectAllowWrongAnswersWhitelisted
  );

  const propertiesEditor = useMemo(() => {
    if (!interaction?.type) return undefined;
    return getInteractionFromFactory(interaction.type)?.PropertiesEditor;
  }, [interaction?.type]);

  const visibleProperties = useMemo(() => {
    if (!interaction?.properties) return [];
    return interaction.properties.filter((property) => {
      if (propertiesEditor?.handledPropertyIds?.includes(property.id)) {
        return false;
      }
      if (
        property.id === "allowWrongAnswers" &&
        !isAllowWrongAnswersWhitelisted
      )
        return false;
      return true;
    });
  }, [
    interaction?.properties,
    isAllowWrongAnswersWhitelisted,
    propertiesEditor,
  ]);

  const customPropertiesView =
    propertiesEditor?.render(interaction, {
      updateInteraction: onUpdateInteraction,
      updateProperty: onChange,
    }) ?? null;

  const views = visibleProperties.map((property, index) => {
    return (
      <InteractionPropertyView
        key={index}
        interactionProperty={property}
        onChange={onChange}
      />
    );
  });

  // renaming
  const onRenameInteraction = (value: string) => {
    if (selectedElementType === "takeSequence") {
      dispatch(
        updateSelectedTakeSequence({
          ...(interaction as ISequence),
          name: value,
        })
      );
      return;
    }

    dispatch(updateSelectedInteraction({ ...interaction, name: value }));
  };

  const renameField = (
    <PiInputfield
      label={t("name")}
      value={interaction.name}
      placeholder={"Interaction name"}
      onChange={onRenameInteraction}
      disabled={interaction.type === InteractionType.DynamicInteraction}
      square
    />
  );

  const [isTranslationLoading, setIsTranslationLoading] = useState(false);
  const builtInVariables = useMemo(
    () => sequenceVariables.filter((variable) => isBuiltInVariable(variable)),
    [sequenceVariables]
  );

  const translateButton = (
    <div className="w-full h-[36px] max-h-[36px]">
      <PiButton
        variant="piBlueOutlined"
        onClick={async () => {
          setIsTranslationLoading(true);
          await translateAll(interaction);
          setIsTranslationLoading(false);
          toast.success("Translations updated!", {
            position: "top-right",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: false,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            theme: "light",
            transition: Bounce,
          });
        }}
      >
        Translate All
      </PiButton>
    </div>
  );

  const isQuizFeedbackOpen = useSelector(selectIsQuizFeedbackOpen);
  const quizFeedbackParentQuizId = useSelector(selectQuizFeedbackParentQuizId);
  const isThisQuizFeedbackOpen =
    isQuizFeedbackOpen && quizFeedbackParentQuizId === interaction.id;

  const quizFeedbackButton = supportsQuizFeedback(interaction) && (
    <div className="w-full h-[36px] max-h-[36px]">
      <PiButton
        variant="piBlueOutlined"
        onClick={() =>
          dispatch(
            isThisQuizFeedbackOpen
              ? closeQuizFeedback()
              : openQuizFeedback(interaction.id)
          )
        }
        data-testid="quiz-feedback-button"
      >
        {t("quiz_feedback") as string}
      </PiButton>
    </div>
  );

  const translateMissingButton = (
    <div className="w-full h-[36px]">
      <PiButton
        variant="piBlueOutlined"
        onClick={async () => {
          setIsTranslationLoading(true);
          await translateAll(interaction, true);
          setIsTranslationLoading(false);
          toast.success("Missing translations updated!", {
            position: "top-right",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: false,
            draggable: false,
            progress: undefined,
            theme: "light",
            transition: Bounce,
            closeButton: false,
          });
        }}
      >
        Translate Missing
      </PiButton>
    </div>
  );

  return (
    <EditorColumn
      testId="interaction-properties-column"
      widthClassName="w-full min-w-0"
      borderSide="left"
      className="interactionpropertiescontent-root"
      header={
        <EditorColumnHeader
          icon={<TouchAppOutlinedIcon fontSize="inherit" />}
          title={interaction?.name}
        />
      }
    >
      {errors?.length > 0 && (
        <InteractionErrorsView
          errors={errors}
          onErrorClick={(nodeId) => {
            if (nodeId) {
              dispatch(setFocusedSequenceNodeId(nodeId));
            }
          }}
        />
      )}
      {warnings?.length > 0 && (
        <InteractionWarningsView warnings={warnings} />
      )}
      <EditorColumnSection
        title="Interaction"
        icon={<TouchAppOutlinedIcon fontSize="inherit" />}
        defaultExpanded
        testId="section-Interaction"
      >
        <EditorColumnFieldGroup>{renameField}</EditorColumnFieldGroup>
      </EditorColumnSection>
      {(customPropertiesView || views.length > 0) && (
        <EditorColumnSection
          title="Properties"
          icon={<SettingsOutlinedIcon fontSize="inherit" />}
          defaultExpanded
          testId="section-Properties"
        >
          <EditorColumnFieldGroup>
            {customPropertiesView}
            {views}
          </EditorColumnFieldGroup>
        </EditorColumnSection>
      )}

      <EditorColumnSection
        title="Actions"
        icon={<GTranslateOutlinedIcon fontSize="inherit" />}
        defaultExpanded
        testId="section-Actions"
      >
        <EditorColumnFieldGroup>
          {isTranslationLoading && (
            <div className="w-full h-[36px] flex items-center justify-center text-creator-primary">
              <CircularProgress color="inherit" />
            </div>
          )}
          {!isTranslationLoading && translateButton}
          {!isTranslationLoading && translateMissingButton}
          {quizFeedbackButton}
        </EditorColumnFieldGroup>
      </EditorColumnSection>
      {interaction.type === InteractionType.Sequence && (
        <>
          <EditorColumnSection
            title="Outliner"
            icon={<AccountTreeOutlinedIcon fontSize="inherit" />}
            defaultExpanded
            testId="section-Outliner"
          >
            <SequenceOutliner />
          </EditorColumnSection>
          <SequenceCanvasActionsPanel />
          {builtInVariables.length > 0 && (
            <EditorColumnSection
              title="Built-in Variables"
              icon={<CodeIcon fontSize="inherit" />}
              testId="section-Built-in Variables"
            >
              <div className="flex flex-col gap-1">
                {builtInVariables.map((variable) => (
                  <VariableItem
                    key={variable.id}
                    variable={variable}
                    isEditing={false}
                    onEdit={() => {}}
                    onSave={() => {}}
                    onCancel={() => {}}
                    onDelete={() => {}}
                    onDuplicate={() => {}}
                    existingVariables={sequenceVariables}
                  />
                ))}
              </div>
            </EditorColumnSection>
          )}
          <EditorColumnSection
            title="Variables"
            icon={<CodeIcon fontSize="inherit" />}
            defaultExpanded
            testId="section-Variables"
          >
            <SequenceVariablesManager
              includeBuiltIns={false}
              showSectionHeaders={false}
            />
          </EditorColumnSection>
        </>
      )}
    </EditorColumn>
  );
}
